import { Button, Typography } from "@mui/material";
import { useDispatch } from "react-redux";

import AppDialog from "components/common/AppDialog";
import TypeToConfirmDialog from "components/common/TypeToConfirmDialog";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureProject,
  useCloseSecureProjectMutation,
  useDeleteSecureProjectMutation,
  useReopenSecureProjectMutation,
} from "features/secureUploads/secureUploads";
import { formatApiError } from "utils/formatApiError";

import { countOf } from "./projectModel";

interface ProjectDialogProps {
  open: boolean;
  project: SecureProject | null;
  onClose: () => void;
}

function useToast() {
  const dispatch = useDispatch();
  return (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));
}

function failure(error: unknown, fallback: string): string {
  return formatApiError((error as { data?: unknown })?.data, fallback);
}

/** Closing keeps every file and every person's access: only the work stops. */
export function CloseProjectDialog({
  open,
  project,
  onClose,
}: ProjectDialogProps) {
  const toast = useToast();
  const [closeProject, { isLoading }] = useCloseSecureProjectMutation();
  if (!project) return null;
  const confirm = async () => {
    try {
      await closeProject(project.id).unwrap();
      toast("Project closed.", "success");
      onClose();
    } catch (error) {
      toast(failure(error, "Could not close the project."), "error");
    }
  };
  return (
    <AppDialog
      open={open}
      onClose={() => !isLoading && onClose()}
      title="Close Project"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirm} disabled={isLoading}>
            Close Project
          </Button>
        </>
      }
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {project.name}
      </Typography>
      <Typography variant="body2">
        This project will be marked as closed and moved out of active work.
        Everyone will keep their access and can still download files.
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
        No one will be able to upload new files or create collections unless it
        is reopened.
      </Typography>
    </AppDialog>
  );
}

export function ReopenProjectDialog({
  open,
  project,
  onClose,
}: ProjectDialogProps) {
  const toast = useToast();
  const [reopenProject, { isLoading }] = useReopenSecureProjectMutation();
  if (!project) return null;
  const confirm = async () => {
    try {
      await reopenProject(project.id).unwrap();
      toast("Project reopened.", "success");
      onClose();
    } catch (error) {
      toast(failure(error, "Could not reopen the project."), "error");
    }
  };
  return (
    <AppDialog
      open={open}
      onClose={() => !isLoading && onClose()}
      title="Reopen Project"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirm} disabled={isLoading}>
            Reopen Project
          </Button>
        </>
      }
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {project.name}
      </Typography>
      <Typography variant="body2">
        This project will be reopened and moved into active work. Please ensure
        the client user access is updated and correct.
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
        Users with access will now be able to upload new files and create new
        collections.
      </Typography>
    </AppDialog>
  );
}

interface DeleteProjectDialogProps extends ProjectDialogProps {
  onDeleted?: (project: SecureProject) => void;
}

/** A project with files asks for its name: they all go to Deleted Files. */
export function DeleteProjectDialog({
  open,
  project,
  onClose,
  onDeleted,
}: DeleteProjectDialogProps) {
  const toast = useToast();
  const [deleteProject, { isLoading, error }] =
    useDeleteSecureProjectMutation();
  if (!project) return null;
  const hasFiles = project.files > 0;
  const confirm = async () => {
    try {
      await deleteProject(project.id).unwrap();
      toast("Project deleted.", "success");
      onDeleted?.(project);
      onClose();
    } catch {
      // The reason shows in the dialog.
    }
  };
  return (
    <TypeToConfirmDialog
      open={open}
      title="Delete Project"
      subject={project.name}
      confirmLabel="Delete Project"
      confirmPhrase={hasFiles ? project.name : null}
      phraseLabel="Type the project name to confirm"
      phrasePlaceholder={project.name}
      busy={isLoading}
      error={error ? failure(error, "Could not delete the project.") : null}
      onCancel={onClose}
      onConfirm={confirm}
    >
      {hasFiles ? (
        <>
          <Typography variant="body2">
            This removes the project and all{" "}
            <b>{countOf(project.files, "file", "files")}</b> from everyone who
            has access, including client users.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Files will be moved to Deleted Files where Admin users can restore
            for <b>60 days.</b>
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          Deleting the project will remove it from everyone who has access,
          including client users.
        </Typography>
      )}
    </TypeToConfirmDialog>
  );
}
